import { FsmPlatform, FsmPlatformStatus } from "@fp/admin-api-client";
import { useListFsmConnections } from "@fp/admin-api-client/fsm-connections/fsm-connections.client";
import { useListFsmPlatforms } from "@fp/admin-api-client/fsm-platforms/fsm-platforms.client";
import {
  PageContentContainer,
} from "@fp/ui/components/base/page-layout/page-layout";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { AdminLayout } from "@/components/admin-layout";
import { ComingSoonFeedbackModal } from "@/components/coming-soon-feedback-modal";
import { AdminPageHeader } from "@/components/admin-page-header";
import { fsmPlatformLabels } from "@/lib/fsm-platform";
import { getConnectionStatus } from "./-components/connection-status";
import { platformPresentation } from "./-components/platform-presentation";
import { PlatformTile } from "./-components/platform-tile";

export const Route = createFileRoute("/integrations/")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { data: connectionsResponse, isSuccess } = useListFsmConnections();
  const { data: platformsResponse } = useListFsmPlatforms();
  const connections = connectionsResponse?.data ?? [];
  const platforms = platformsResponse?.data ?? [];
  const [comingSoonPlatform, setComingSoonPlatform] = useState<FsmPlatform | null>(null);

  const connectionFor = (platform: FsmPlatform) =>
    connections.find((c) => c.platform === platform);

  const byLabel = (a: { platform: FsmPlatform }, b: { platform: FsmPlatform }) =>
    fsmPlatformLabels[a.platform].localeCompare(fsmPlatformLabels[b.platform]);

  const connectable = platforms
    .filter((p) => p.status !== FsmPlatformStatus.ComingSoon)
    .sort(byLabel);
  const comingSoon = platforms
    .filter((p) => p.status === FsmPlatformStatus.ComingSoon)
    .sort(byLabel);

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Integrations"
        parent={{ url: "/dashboard", label: "Home" }}
      />
      <PageContentContainer>
        <p className="text-sm text-gray-500 mb-6">
          Connect your field service management platform to automatically create
          payouts when jobs are completed.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {connectable.map((p) => (
            <PlatformTile
              key={p.platform}
              name={fsmPlatformLabels[p.platform]}
              description={platformPresentation[p.platform].description}
              status={isSuccess ? getConnectionStatus(connectionFor(p.platform)) : undefined}
              isAvailable
              logoUrl={platformPresentation[p.platform].logoUrl}
              onClick={() => {
                const route = platformPresentation[p.platform].route;

                if (route) {
                  navigate({ to: route });
                }
              }}
            />
          ))}
        </div>
        {comingSoon.length > 0 && (
          <>
            <div className="flex items-center gap-3 my-6">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs font-medium uppercase tracking-wider text-gray-400">
                Coming soon
              </span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {comingSoon.map((p) => (
                <PlatformTile
                  key={p.platform}
                  name={fsmPlatformLabels[p.platform]}
                  description={platformPresentation[p.platform].description}
                  isAvailable={false}
                  logoUrl={platformPresentation[p.platform].logoUrl}
                  onClick={() => setComingSoonPlatform(p.platform)}
                />
              ))}
            </div>
          </>
        )}
      </PageContentContainer>
      <ComingSoonFeedbackModal
        isOpen={comingSoonPlatform !== null}
        onClose={() => setComingSoonPlatform(null)}
        feature={comingSoonPlatform ?? ""}
        featureLabel={comingSoonPlatform ? fsmPlatformLabels[comingSoonPlatform] : ""}
      />
    </AdminLayout>
  );
}
